import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { updateUserProfile } from '../../services/domain/authService';
import PageTitle from '../../components/common/PageTitle';
import { User, Mail, Calendar, Edit2, Save, Bookmark, Bell } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ROUTES } from '../../constants/routes';

const ProfilePage: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const [editMode, setEditMode] = useState(false);
  const [name, setName] = useState(user?.name || '');
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');
  
  // Update name when user changes
  useEffect(() => {
    if (user) {
      setName(user.name || '');
    }
  }, [user]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccessMessage('');
    
    try {
      if (!user?.id) {
        throw new Error('User ID not found');
      }
      
      await updateUserProfile(user.id, { name });
      await refreshUser();
      setEditMode(false);
      setSuccessMessage('Profile updated successfully');
      
      // Clear success message after a few seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (error) {
      console.error('Failed to update profile:', error);
      setError('Failed to update profile. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  if (!user) {
    return (
      <div className="container-custom py-8">
        <div className="text-center">Loading profile...</div>
      </div>
    );
  }
  
  return (
    <div className="container-custom py-8">
      <PageTitle title="My Profile" />
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-serif font-bold">Profile Information</h2>
              {!editMode ? (
                <button 
                  className="btn btn-outline flex items-center"
                  onClick={() => setEditMode(true)}
                >
                  <Edit2 size={16} className="mr-2" />
                  Edit Profile
                </button>
              ) : (
                <button 
                  className="text-neutral-600 hover:text-neutral-900"
                  onClick={() => {
                    setEditMode(false);
                    setName(user.name || '');
                  }}
                >
                  Cancel
                </button>
              )}
            </div>
            
            {successMessage && (
              <div className="mb-4 p-3 bg-green-50 text-green-700 rounded-md border border-green-100">
                {successMessage}
              </div>
            )}
            
            {error && (
              <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md border border-red-100">
                {error}
              </div>
            )}
            
            {editMode ? (
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label htmlFor="name" className="block mb-1 text-sm font-medium">
                    Full Name
                  </label>
                  <input 
                    type="text"
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="form-input w-full px-3 py-2 border border-neutral-300 rounded-md"
                  />
                </div>
                
                <div className="mb-4">
                  <label htmlFor="email" className="block mb-1 text-sm font-medium">
                    Email Address
                  </label>
                  <input 
                    type="email"
                    id="email"
                    value={user.email}
                    disabled
                    className="form-input w-full px-3 py-2 bg-neutral-50 border border-neutral-300 rounded-md cursor-not-allowed"
                  />
                  <p className="text-xs text-neutral-500 mt-1">Email cannot be changed</p>
                </div>
                
                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn btn-primary flex items-center"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full"></div>
                      Updating...
                    </>
                  ) : (
                    <>
                      <Save size={16} className="mr-2" />
                      Save Changes
                    </>
                  )}
                </button>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-full bg-neutral-100 flex items-center justify-center mr-3">
                    <User size={20} className="text-neutral-600" />
                  </div>
                  <div>
                    <p className="text-sm text-neutral-600">Name</p>
                    <p className="font-medium">{user.name || 'Not set'}</p>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-full bg-neutral-100 flex items-center justify-center mr-3">
                    <Mail size={20} className="text-neutral-600" />
                  </div>
                  <div>
                    <p className="text-sm text-neutral-600">Email</p>
                    <p className="font-medium">{user.email}</p>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-full bg-neutral-100 flex items-center justify-center mr-3">
                    <Calendar size={20} className="text-neutral-600" />
                  </div>
                  <div>
                    <p className="text-sm text-neutral-600">Member Since</p>
                    <p className="font-medium">
                      {user.createdAt 
                        ? formatDistanceToNow(new Date(user.createdAt), { addSuffix: true })
                        : 'Recently'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div>
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h3 className="text-lg font-serif font-bold mb-4">Subscription</h3>
            <div className="mb-5">
              <p className="text-sm text-neutral-600 mb-1">Status</p>
              <p className="font-medium">
                {user?.isPremium ? (
                  <span className="text-green-600">Premium Member</span>
                ) : (
                  <span>Free Account</span>
                )}
              </p>
            </div>
            {!user?.isPremium && (
              <Link to={ROUTES.SUBSCRIBE || "/subscribe-premium"} className="btn btn-primary w-full">
                Upgrade to Premium
              </Link>
            )}
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-serif font-bold mb-3">Quick Links</h3>
            <ul className="space-y-3">
              <li>
                <Link 
                  to={ROUTES.SAVED_ARTICLES || "/saved-articles"}
                  className="flex items-center text-neutral-700 hover:text-primary-600"
                >
                  <Bookmark size={16} className="mr-3" />
                  <span>Saved Articles</span>
                </Link>
              </li>
              <li>
                <Link 
                  to="/notifications"
                  className="flex items-center text-neutral-700 hover:text-primary-600"
                >
                  <Bell size={16} className="mr-3" />
                  <span>Notification Settings</span>
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;